import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Loader2, Lock, FlaskConical } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { CompoundCard } from "@/components/CompoundCard";
import { AiSummary, StudyList, DoseNote } from "@/components/evidence/shared";

const Results = ({ data, t }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="condition-results">
    <AiSummary text={data.ai_summary} stage={data.evidence_stage} t={t} />
    <h3 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-gray-500"><FlaskConical size={15} /> {t("cond.compounds")}</h3>
    <DoseNote t={t} />
    {(data.compounds || []).length === 0 && <p className="text-sm text-gray-500">{t("cond.none")}</p>}
    <div className="space-y-3" data-testid="condition-compounds-list">
      {(data.compounds || []).map((c, i) => <CompoundCard key={`${c.name}-${i}`} c={c} i={i} t={t} />)}
    </div>
    <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400">{t("pubmed.studies")}</h3>
    <StudyList studies={data.studies || []} />
  </motion.div>
);

export const ConditionResearch = ({ conditions, user, t, lang }) => {
  const navigate = useNavigate();
  const isPro = !!user?.is_pro;
  const [active, setActive] = useState(null);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const open = async (slug, customQ) => {
    if (!isPro && slug) { navigate(`/condition/${slug}`); return; }
    setActive(slug || "custom"); setData(null);
    if (!isPro) return;
    setLoading(true);
    try {
      const { data } = customQ
        ? await api.get(`/pubmed/condition-custom`, { params: { q: customQ, language: lang } })
        : await api.get(`/pubmed/condition/${slug}`, { params: { language: lang } });
      setData(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="condition-research-section">
      <div className="flex flex-wrap gap-2">
        {conditions.map((c) => (
          <button key={c.slug} data-testid={`pubmed-condition-tab-${c.slug}`} onClick={() => open(c.slug)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active === c.slug ? "bg-emerald-500 text-white" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}>
            {lang === "es" ? c.name_es : c.name_en}
          </button>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (custom.trim().length >= 3) open(null, custom.trim()); }} className="mt-4 flex gap-2">
        <input data-testid="condition-custom-input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={t("cond.customPh")}
          className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100" />
        <button data-testid="condition-custom-button" disabled={loading}
          className="rounded-full bg-gray-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-gray-800 disabled:opacity-60"><Search size={16} /></button>
      </form>
      {active && !isPro && (
        <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="pubmed-premium-locked-notice">
          <Lock className="mx-auto mb-2 text-amber-500" size={28} />
          <p className="mb-3 text-sm font-semibold text-amber-800">{t("pubmed.premiumLock")}</p>
          <Link to="/pricing" className="inline-block rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-600">{t("pubmed.upgrade")}</Link>
        </div>
      )}
      {loading && <p data-testid="condition-loading" className="mt-5 flex items-center gap-2 text-sm text-gray-500"><Loader2 size={15} className="animate-spin" /> {t("cond.analyzing")}</p>}
      {data && <div className="mt-5"><Results data={data} t={t} /></div>}
    </section>
  );
};
